import { useState } from 'react';
import { useQuery } from 'urql';
import { queryCategoriesQuery } from '../../graphql/services';

interface ImageLoadingState {
  [key: number]: boolean;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  image?: {
    secure_url: string;
  };
}

interface UseCategorySelectionProps {
  selectedCategories: Category[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  itemsPerPage: number;
}

interface UseCategorySelectionReturn {
  imageLoading: ImageLoadingState;
  currentCursor: string | null;
  direction: 'forward' | 'backward';
  expandedDescriptionId: string | null;
  queryResult: any; // Replace 'any' with actual query result type
  handleImageLoad: (categoryId: number) => void;
  handleImageLoadStart: (categoryId: number) => void;
  toggleCategory: (category: Category) => void;
  handlePageChange: (direction: 'next' | 'previous') => void;
  setExpandedDescriptionId: (id: string | null) => void;
  shouldShowReadMore: (text: string) => boolean;
}

const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 50;

export function useCategorySelection({
  selectedCategories,
  setSelectedCategories,
  itemsPerPage
}: UseCategorySelectionProps): UseCategorySelectionReturn {
  const [imageLoading, setImageLoading] = useState<ImageLoadingState>({});
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [expandedDescriptionId, setExpandedDescriptionId] = useState<string | null>(null);

  const paginationVariables = {
    filter: { isDeleted: { is: false } },
    paging: direction === 'forward'
      ? { first: itemsPerPage, after: currentCursor }
      : { last: itemsPerPage, before: currentCursor },
  };

  const [queryResult] = useQuery({
    query: queryCategoriesQuery,
    variables: currentCursor
      ? paginationVariables
      : {
          filter: { isDeleted: { is: false } },
          paging: { first: itemsPerPage }
        },
  });

  const toggleCategory = (category: Category) => {
    setSelectedCategories((prev) =>
      prev.some((c) => c.id === category.id)
        ? prev.filter((c) => c.id !== category.id)
        : [...prev, category]
    );
  };

  const handleImageLoad = (categoryId: number) => {
    setImageLoading((prev) => ({
      ...prev,
      [categoryId]: false,
    }));
  };

  const handleImageLoadStart = (categoryId: number) => {
    setImageLoading((prev) => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  const handleNextPage = () => {
    if (queryResult.data?.serviceCategories.pageInfo.hasNextPage) {
      setDirection('forward');
      setCurrentCursor(queryResult.data.serviceCategories.pageInfo.endCursor);
    }
  };

  const handlePreviousPage = () => {
    if (queryResult.data?.serviceCategories.pageInfo.hasPreviousPage) {
      setDirection('backward');
      setCurrentCursor(queryResult.data.serviceCategories.pageInfo.startCursor);
    }
  };

  const handlePageChange = (direction: 'next' | 'previous') => {
    if (direction === 'next') {
      handleNextPage();
    } else {
      handlePreviousPage();
    }
  };

  const shouldShowReadMore = (text: string) => {
    return text.length > DESCRIPTION_MAX_LENGTH;
  };

  return {
    imageLoading,
    currentCursor,
    direction,
    expandedDescriptionId,
    queryResult,
    handleImageLoad,
    handleImageLoadStart,
    toggleCategory,
    handlePageChange,
    setExpandedDescriptionId,
    shouldShowReadMore,
  };
}
